import React, { useMemo } from 'react';
import { reduxForm, submit, reset } from 'redux-form';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { validateImportForm } from './utils';
import saga from './saga';
import reducer from './reducer';
import { getIsOrderImportFormOpenSelector, getIsImportedFileLoadingSelector } from './selectors';
import { useInjectSaga } from '../../utils/injectSaga';
import { useInjectReducer } from '../../utils/injectReducer';
import { ORDER_IMPORT_FORM_NAME } from './constants';
import { getDownloadOrdersTemplateAction, getImportFileAction, resetStateAction } from './actions';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import { OrderImportFormView } from './components/order-form-import-view';
import closeModal from '../../utils/closeModal';

const OrderImportFormWrapper = ({
	onDownloadTemplate,
	isOrderImportFormOpen,
	onFormSubmit,
	onImportFile,
	onResetState,
	resetForm,
	isLoadingImportForm,
}) => {
	if (!isOrderImportFormOpen) return null;
	useInjectSaga({ key: 'ordersImportsModalSaga', saga });
	useInjectReducer({ key: 'ordersImportsModalReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	const handleModalClose = () => {
		closeModal(query);
		resetForm();
		onResetState();
		history.push(`${pathname}?${query}`);
	};

	const modalContent = useMemo(() => {
		return <OrderImportFormView onDownloadTemplate={onDownloadTemplate} />;
	}, []);

	const handleImportFileFormSubmit = async () => {
		await onFormSubmit(ORDER_IMPORT_FORM_NAME);
		onImportFile({ redirect: handleModalClose });
	};

	const headerText = useMemo(() => {
		return 'Импорт заказов';
	}, [isOrderImportFormOpen]);

	const rightButtons = useMemo(() => {
		return [
			{
				onClick: handleImportFileFormSubmit,
				text: 'Сохранить',
				color: 'primary',
				loading: isLoadingImportForm,
				disabled: isLoadingImportForm,
			},
			{
				onClick: handleModalClose,
				text: 'Закрыть',
				color: 'secondary',
				disabled: isLoadingImportForm,
			},
		];
	}, [isLoadingImportForm, handleImportFileFormSubmit, handleModalClose]);

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleImportFileFormSubmit();
		}
	};

	return (
		<ModalWindow
			isModalShown={isOrderImportFormOpen}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			headerText={headerText}
			modalContent={modalContent}
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalClose, text: 'order' }]}
			footerButtons={<ModalFooterButtons rightButtons={rightButtons} />}
		/>
	);
};

const withForm = reduxForm({
	form: ORDER_IMPORT_FORM_NAME,
	initialValues: {
		file: null,
	},
	enableReinitialize: true,
	onSubmit: validateImportForm,
})(OrderImportFormWrapper);

const mapStateToProps = createStructuredSelector({
	isOrderImportFormOpen: getIsOrderImportFormOpenSelector(),
	isLoadingImportForm: getIsImportedFileLoadingSelector(),
});

const mapDispatchToProps = {
	onResetState: resetStateAction,
	onImportFile: getImportFileAction,
	onDownloadTemplate: getDownloadOrdersTemplateAction,
	onFormSubmit: submit,
	resetForm: () => reset(ORDER_IMPORT_FORM_NAME),
};

const OrderImportFormContainer = connect(mapStateToProps, mapDispatchToProps)(withForm);

OrderImportFormWrapper.propTypes = {
	isLoadingImportForm: PropTypes.bool.isRequired,
	isOrderImportFormOpen: PropTypes.bool.isRequired,
	onResetState: PropTypes.func.isRequired,
	onDownloadTemplate: PropTypes.func.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	onImportFile: PropTypes.func.isRequired,
	resetForm: PropTypes.func.isRequired,
};

export default OrderImportFormContainer;
